// SessionInvitePage.jsx - Public landing page for session invite links
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { analyticsService } from "../../services/analytics";
import logger from "../../utils/logger";

/**
 * Public page rendered at /session-invite/:token
 * No authentication required to view.
 * Displays session preview and CTAs to register or log in.
 */
function SessionInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();

    const fetchInvite = async () => {
      try {
        const res = await httpRequest(`${API_BASE}/invites/session/${token}`, {
          method: "GET",
        });
        if (res.ok) {
          const data = await res.json();
          setInvite(data);
          analyticsService.track("session_invite_viewed", { token, status: data.status });
        } else if (res.status === 404) {
          setError("not_found");
        } else {
          setError("server_error");
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          logger.error("Failed to fetch invite:", err);
          setError("server_error");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchInvite();
    return () => controller.abort();
  }, [token]);

  const handleRegister = () => {
    navigate(`/login?register=true&invite_token=${token}`);
  };

  const handleLogin = () => {
    navigate(`/login?invite_token=${token}`);
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ color: "var(--text-secondary, #666)", textAlign: "center" }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={iconStyle}>🎲</div>
          <h2 style={headingStyle}>Invite not found</h2>
          <p style={bodyStyle}>This invite link doesn&apos;t exist or has been removed.</p>
          <button type="button" onClick={() => navigate("/")} style={primaryBtnStyle}>
            Explore Pista
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={iconStyle}>⚠️</div>
          <h2 style={headingStyle}>Something went wrong</h2>
          <p style={bodyStyle}>We couldn&apos;t load this invite. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (!invite) return null;

  if (invite.status === "claimed") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={iconStyle}>✅</div>
          <h2 style={headingStyle}>Already claimed</h2>
          <p style={bodyStyle}>
            This session has already been claimed. Want to track your own plays?
          </p>
          <button type="button" onClick={handleRegister} style={primaryBtnStyle}>
            Join Pista
          </button>
        </div>
      </div>
    );
  }

  if (invite.status === "expired") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={iconStyle}>⏱️</div>
          <h2 style={headingStyle}>Invite expired</h2>
          <p style={bodyStyle}>This invite link has expired. Ask your friend to resend it.</p>
          <button type="button" onClick={handleRegister} style={primaryBtnStyle}>
            Join Pista anyway
          </button>
        </div>
      </div>
    );
  }

  // Active pending invite — main flow
  const formattedDate = invite.date
    ? new Date(invite.date).toLocaleDateString(undefined, { dateStyle: "long" })
    : null;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Game image + name */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
          {invite.game_image && (
            <img
              src={invite.game_image}
              alt={invite.game_name}
              style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }}
            />
          )}
          <div>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--text-secondary, #666)", fontWeight: 500 }}>
              {invite.inviter_name} played
            </p>
            <h2 style={{ margin: "0.1rem 0 0", fontSize: "1.2rem", fontWeight: "700", color: "var(--text-primary, #333)" }}>
              {invite.game_name}
            </h2>
            {formattedDate && (
              <p style={{ margin: "0.1rem 0 0", fontSize: "0.78rem", color: "var(--text-secondary, #666)" }}>
                {formattedDate}
              </p>
            )}
          </div>
        </div>

        {/* Players */}
        {invite.participants && invite.participants.length > 0 && (
          <div
            style={{
              backgroundColor: "var(--bg-secondary, #f5f5f5)",
              borderRadius: "8px",
              padding: "0.75rem",
              marginBottom: "1.25rem",
            }}
          >
            <p style={{ margin: "0 0 0.4rem", fontSize: "0.75rem", fontWeight: "600", color: "var(--text-secondary, #666)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Players
            </p>
            {invite.participants.map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.2rem 0" }}>
                <span style={{
                  fontSize: "0.9rem",
                  color: "var(--text-primary, #333)",
                  fontWeight: p.username === invite.guest_name ? "700" : "400",
                }}>
                  {p.username}
                  {p.username === invite.guest_name && (
                    <span style={{ marginLeft: "0.4rem", fontSize: "0.7rem", color: "var(--pista-button-bg, #4caf50)", fontWeight: "600" }}>← you</span>
                  )}
                </span>
                {p.score !== undefined && p.score !== null && p.score !== "" && (
                  <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--text-secondary, #666)" }}>{p.score}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CTAs */}
        <button type="button" onClick={handleRegister} style={primaryBtnStyle}>
          Claim this session &amp; join Pista
        </button>

        <p style={{ margin: "1rem 0 0", textAlign: "center", fontSize: "0.85rem", color: "var(--text-secondary, #666)" }}>
          Already have an account?{" "}
          <button type="button" onClick={handleLogin} style={linkBtnStyle}>
            Log in
          </button>
        </p>

        {/* Brand footer */}
        <p style={{ marginTop: "1.5rem", textAlign: "center", fontSize: "0.75rem", color: "var(--text-secondary, #aaa)" }}>
          Pista · Track your board game sessions
        </p>
      </div>
    </div>
  );
}

// Styles using CSS variables for dark-mode compatibility
const containerStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
  backgroundColor: "var(--bg-primary, #fff)",
};

const cardStyle = {
  width: "100%",
  maxWidth: "420px",
  backgroundColor: "var(--bg-primary, #fff)",
  border: "1px solid var(--border-color, #e0e0e0)",
  borderRadius: "16px",
  padding: "2rem 1.5rem",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
};

const iconStyle = {
  fontSize: "2.5rem",
  textAlign: "center",
  marginBottom: "0.75rem",
};

const headingStyle = {
  margin: "0 0 0.5rem",
  fontSize: "1.2rem",
  fontWeight: "700",
  color: "var(--text-primary, #333)",
  textAlign: "center",
};

const bodyStyle = {
  margin: "0 0 1.25rem",
  fontSize: "0.9rem",
  color: "var(--text-secondary, #666)",
  lineHeight: 1.5,
  textAlign: "center",
};

const primaryBtnStyle = {
  display: "block",
  width: "100%",
  padding: "0.85rem",
  backgroundColor: "var(--pista-button-bg, #4caf50)",
  color: "var(--pista-button-text, white)",
  border: "none",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: "700",
  cursor: "pointer",
  textAlign: "center",
};

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "var(--pista-button-bg, #4caf50)",
  fontWeight: "600",
  cursor: "pointer",
  fontSize: "inherit",
  padding: 0,
  textDecoration: "underline",
};

export default SessionInvitePage;
